import type { TableRect } from '@tiptap/pm/tables';
import { selectedRect, CellSelection } from '@tiptap/pm/tables';
import type {
  Node as ProsemirrorModelNode,
  Schema,
  Slice,
  Mark,
  Attrs,
  ResolvedPos,
} from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import { TextSelection, EditorState } from '@tiptap/pm/state';
import type { NodeWithPos, Editor } from '@tiptap/vue-2';
import { findChildren } from '@tiptap/vue-2';
import type { Field, MergeValues } from '@getaccept/editor-lib';
import {
  Context,
  EditorHelper,
  FieldType,
  MarkType,
  MergeCategory,
  NodeAlign,
  ProsemirrorType,
} from '@getaccept/editor-lib';
import type {
  Text,
  HardBreak,
  ListItem,
  Paragraph,
  Heading,
  BulletList,
  OrderedList,
  TextContentDoc,
} from '@getaccept/lib-shared/src/types/text-content';
import { nanoid } from 'nanoid';
import type { AlignList, SetHeading, SetParagraph, ProsemirrorNode } from '../types';

export class ProsemirrorHelper {
  static selectTextNode(transaction: Transaction, position: number): Transaction {
    return transaction.setSelection(TextSelection.create(transaction.doc, position));
  }

  static selectTextNearPosition(transaction: Transaction, position: number): Transaction {
    const resolvedPosition = transaction.doc.resolve(position);
    return transaction.setSelection(TextSelection.near(resolvedPosition));
  }

  static deleteNode(transaction: Transaction, position: number, nodeSize: number): Transaction {
    return transaction.delete(position, position + nodeSize);
  }

  static createEditorStateFromSlice(slice: Slice, schema: Schema): EditorState {
    const state: EditorState = EditorState.create({ schema });
    const transaction: Transaction = state.tr.replace(0, state.doc.content.size, slice);
    return state.apply(transaction);
  }

  static isCompleteTableSelected(state: EditorState): boolean {
    const { left, right, map, top, bottom }: TableRect = selectedRect(state);
    return left === 0 && right === map.width && top === 0 && map.height === bottom;
  }

  static getNodeAttributesFromPosition(
    transaction: Transaction,
    position: number
  ): Attrs | undefined {
    const node: ProsemirrorModelNode = transaction.doc.nodeAt(position);

    if (!node) {
      return;
    }

    return node.attrs;
  }

  static getNodeBeforePosition(transaction: Transaction, position: number): ProsemirrorModelNode {
    const resolvedPosition: ResolvedPos = transaction.doc.resolve(position);
    return resolvedPosition.nodeBefore;
  }

  static getNodeAfterPosition(transaction: Transaction, position: number): ProsemirrorModelNode {
    const resolvedPosition: ResolvedPos = transaction.doc.resolve(position);
    return resolvedPosition.nodeAfter;
  }

  static updateNodeAttributes(
    transaction: Transaction,
    schema: Schema,
    position: number,
    nodeType: ProsemirrorType,
    attributes: Attrs
  ): Transaction | undefined {
    const attrs = ProsemirrorHelper.getNodeAttributesFromPosition(transaction, position);

    if (!attrs) {
      return;
    }

    return transaction.setNodeMarkup(position, schema.nodes[nodeType], {
      ...attrs,
      ...attributes,
    });
  }

  static alignList(
    { state, view: { dispatch } }: Editor,
    { align, position }: AlignList,
    type: ProsemirrorType
  ) {
    const { tr, schema, doc } = state;
    const listNode: ProsemirrorModelNode = doc.nodeAt(position);

    if (!listNode) {
      return;
    }

    const nodes: NodeWithPos[] = findChildren(listNode, (node: ProsemirrorModelNode) =>
      [ProsemirrorType.Paragraph, ProsemirrorType.Heading].includes(
        node.type.name as ProsemirrorType
      )
    );

    nodes.forEach(({ pos, node }: NodeWithPos) => {
      const nodePosition = position + pos + 1;
      const nodeAttributes = ProsemirrorHelper.getNodeAttributesFromPosition(tr, nodePosition);

      tr.setNodeMarkup(nodePosition, schema.nodes[node.type.name], { ...nodeAttributes, align });
    });

    dispatch(
      tr.setNodeMarkup(position, schema.nodes[type], {
        align,
      })
    );
  }

  static getLinkRangeAroundSelection(
    { schema, doc }: EditorState,
    pos: number
  ): { from: number; to: number } {
    const resolvedPos = doc.resolve(pos);

    const { parent, parentOffset } = resolvedPos;
    const start = parent.childAfter(parentOffset);
    if (!start.node) {
      return null;
    }

    const link = start.node.marks.find(mark => mark.type === schema.marks.link);
    if (!link) {
      return null;
    }

    let startIndex = resolvedPos.index();
    let startPos: number = resolvedPos.start() + start.offset;
    let endIndex = startIndex + 1;
    let endPos: number = startPos + start.node.nodeSize;
    while (startIndex > 0 && link.isInSet(parent.child(startIndex - 1).marks)) {
      startIndex -= 1;
      startPos -= parent.child(startIndex).nodeSize;
    }
    while (endIndex < parent.childCount && link.isInSet(parent.child(endIndex).marks)) {
      endPos += parent.child(endIndex).nodeSize;
      endIndex += 1;
    }
    return { from: startPos, to: endPos };
  }

  static getTagIds(content = ''): string[] {
    if (!content) {
      return [];
    }
    return content
      .split('"tagId":"')
      .slice(1)
      .filter(tagId => !tagId.startsWith('"')) // remove empty tagIds
      .map(id => id.substring(0, EditorHelper.NANO_ID_LENGTH));
  }

  static isCellSelection(value: unknown): value is CellSelection {
    return value instanceof CellSelection;
  }

  static setTextType(
    { state: { doc, tr, schema }, view: { dispatch } }: Editor,
    payload: SetParagraph | SetHeading,
    type: ProsemirrorType
  ) {
    const { position, ...attributes } = payload;

    const node = doc.nodeAt(position);

    if (!node) {
      return;
    }

    const transaction: Transaction = tr.setBlockType(
      position,
      position + node.nodeSize,
      schema.nodes[type],
      {
        ...attributes,
      }
    );

    dispatch(transaction);
  }

  static alignText(
    { state: { tr, schema }, view: { dispatch } }: Editor,
    payload: SetParagraph | SetHeading,
    type: ProsemirrorType
  ) {
    const { position, ...attributes } = payload;

    const transaction: Transaction = ProsemirrorHelper.updateNodeAttributes(
      tr,
      schema,
      position,
      type,
      { ...attributes }
    );

    dispatch(transaction);
  }

  static replaceMergeTagIds(
    docNode: ProsemirrorModelNode,
    fields: Field[]
  ): [ProsemirrorModelNode, Field[]] {
    const state = EditorState.create({
      doc: docNode,
    });
    const { tr, schema, doc } = state;

    let updatedTags: Field[] = [];
    let transaction = tr;

    const mergeNodes = findChildren(doc, node => node.type.name === ProsemirrorType.MergeTag);

    mergeNodes.forEach(({ node, pos }) => {
      const currentTag = fields.find(({ id }) => id === node?.attrs?.tagId);

      if (!currentTag) {
        return;
      }

      const newId = nanoid(EditorHelper.NANO_ID_LENGTH);

      updatedTags = [...updatedTags, { ...currentTag, id: newId }];

      transaction = ProsemirrorHelper.updateNodeAttributes(
        transaction,
        schema,
        pos,
        ProsemirrorType.MergeTag,
        {
          tagId: newId,
        }
      );
    });

    const updatedState = state.apply(transaction);

    return [updatedState.doc, updatedTags];
  }

  static hasLinkBeforeOrAfterCursor({ doc, selection }: EditorState): boolean {
    const nodeBefore: ProsemirrorModelNode = doc.nodeAt(selection.anchor - 1);
    const nodeAfter: ProsemirrorModelNode = doc.nodeAt(selection.anchor + 1);
    const hasLinkBefore = !!nodeBefore?.marks.find((mark: Mark) => mark.attrs['href']);
    const hasLinkAfter =
      !hasLinkBefore && !!nodeAfter?.marks.find((mark: Mark) => mark.attrs['href']);

    return hasLinkAfter || hasLinkBefore;
  }

  static createLinkWithUrlOnly(
    { state: { doc, selection, schema, tr }, view: { dispatch } }: Editor,
    href: string
  ) {
    const activeMarks = doc.nodeAt(selection.anchor)?.marks || [];

    const node: ProsemirrorModelNode = schema.text(href, [
      ...activeMarks,
      schema.marks.link.create({ href }),
    ]);

    dispatch(tr.replaceSelectionWith(node, false));
  }

  static getActiveLinkRange(state: EditorState, position: number): { from: number; to: number } {
    return (
      ProsemirrorHelper.getLinkRangeAroundSelection(state, position) ||
      ProsemirrorHelper.getLinkRangeAroundSelection(state, position - 1)
    );
  }

  static hasMarksWithoutMarkdownSupport(content: string) {
    const doc: { content: ProsemirrorModelNode[] } = JSON.parse(content);
    return doc.content.some(({ type, attrs, content }) => {
      if ([ProsemirrorType.Heading, ProsemirrorType.Paragraph].includes(type as any)) {
        if (attrs && attrs.align && attrs.align !== NodeAlign.Left) {
          return true;
        }

        return ((content as unknown) as ProsemirrorModelNode[])?.some(({ type, marks }) => {
          if ((type as any) === ProsemirrorType.Text) {
            return (marks || []).some(({ type }) =>
              [MarkType.Strike, MarkType.Superscript, MarkType.Underline].includes(type as any)
            );
          }
        });
      }
      return false;
    });
  }

  // TODO: take list of supported formatting options
  static getMarkdownSafeNode(
    node:
      | TextContentDoc
      | Heading
      | Paragraph
      | BulletList
      | OrderedList
      | Text
      | ListItem
      | HardBreak
  ) {
    if (node.type === ProsemirrorType.Heading) {
      if (node.attrs?.level === 7) {
        return {
          type: ProsemirrorType.Paragraph,
          content: node.content?.map(ProsemirrorHelper.getMarkdownSafeNode),
        };
      }
      return {
        ...node,
        attrs: { level: node.attrs?.level || 1 },
        content: node.content?.map(ProsemirrorHelper.getMarkdownSafeNode),
      };
    }

    if (node.type === ProsemirrorType.Text) {
      const remainingMarks = (node.marks || []).filter(mark =>
        [MarkType.Bold, MarkType.Italic, MarkType.Link].includes(mark.type)
      );

      if (remainingMarks.length === 0) {
        return { type: node.type, text: node.text };
      }

      return {
        ...node,
        marks: remainingMarks,
      };
    }

    if ('content' in node) {
      return {
        type: node.type,
        content: (node.content as Text[]).map(ProsemirrorHelper.getMarkdownSafeNode),
      };
    }

    return node;
  }

  static getContentWithSupportedMarkdownFormattingOptions(docString: string): string {
    const currentDoc: TextContentDoc = JSON.parse(docString);

    const newDoc = ProsemirrorHelper.getMarkdownSafeNode(currentDoc);

    return JSON.stringify(newDoc);
  }

  static getFirstPositionInSelection = (selection: TextSelection) => {
    const { $anchor, $head } = selection;
    return $head.pos > $anchor.pos ? $anchor.pos : $head.pos;
  };

  static getFallbackMergeTag = (attrs: Attrs, allCustomTags: Field[]) => ({
    category: attrs?.category || undefined,
    mergeKey: attrs?.mergeKey || undefined,
    type: attrs?.customName ? FieldType.Custom : FieldType.Merge,
    id: '',
    customName: attrs?.customName || undefined,
    value: attrs?.customName
      ? allCustomTags.find(({ customName }) => customName === attrs.customName)?.value
      : undefined,
  });

  static getUpdatedMergeTagOptions = (editor: Editor, mergeValues: MergeValues, fields: Field[]) =>
    editor.options.extensions.map(extension =>
      extension.name === ProsemirrorType.MergeTag
        ? {
            ...extension,
            options: {
              ...extension.options,
              mergeValues,
              fields,
            },
          }
        : extension
    );

  static updateFieldsWithMarkdown = (fields: Field[], markdownFieldId: string, content: string) =>
    fields.map(field => ({
      ...field,
      value:
        field.id === markdownFieldId
          ? ProsemirrorHelper.getContentWithSupportedMarkdownFormattingOptions(content)
          : field.value,
    }));

  static isTagNotAllowedInResource = (context: Context, currentTag: Field) => {
    const isRecipientTagAndResource =
      [Context.DsrResource, Context.Resource].includes(context) &&
      currentTag.category === MergeCategory.Recipient;

    const isCustomTagAndContractResource =
      Context.Resource === context && currentTag.type === FieldType.Custom;

    return isRecipientTagAndResource || isCustomTagAndContractResource;
  };

  static filterNodesByType = (nodes: ProsemirrorNode[], nodeType: ProsemirrorType) =>
    nodes.filter(({ type }: ProsemirrorNode) => type === nodeType) || [];
}
